// Global upload queue. Lives above the router so uploads survive navigation.
// Feeds three backend endpoints:
//   • media (video/audio)   → /candidates/{id}/upload-recording  (Meeting + STT)
//   • attachment            → /candidates/{id}/attachments
//   • tool-transcribe       → /tools/transcribe                  (returns job_id)
// The `candidateId` field is repurposed as a generic context key: for tool
// uploads we use TOOL_CTX so ToolsPage can subscribe. For tool uploads the
// server response is a job_id (not the final transcript) — ToolsPage picks it
// up via subscribe() and continues polling in localStorage.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { isMediaFile } from "./mime";
import { uploadErrorText, xhrUploadCancellable, type UploadHandle } from "./uploader";
import { ApiError } from "@/api/client";
import { holdReload } from "@/lib/appUpdate";
import { captureToolUploadResponse } from "@/pages/Tools/toolJobsStore";

export type UploadStatus = "queued" | "uploading" | "done" | "failed" | "cancelled";
export type UploadKind = "media" | "attachment" | "tool-transcribe";

export const TOOL_CTX = "__tools__";

export interface UploadItem {
  id: string;
  file: File;
  candidateId: string;
  kind: UploadKind;
  progress: number;
  status: UploadStatus;
  error: string | null;
  startedAt: number;
  extraFields?: Record<string, string>;
}

export interface CompletionEvent {
  candidateId: string;
  kind: UploadKind;
  response: unknown;
}

interface UploadContextValue {
  items: UploadItem[];
  // `mediaFields` go along with media (recording) uploads only — e.g. the
  // speaker count; attachments ignore them.
  enqueue: (
    files: FileList | File[],
    candidateId: string,
    mediaFields?: Record<string, string>,
  ) => void;
  enqueueTool: (files: FileList | File[], extraFields: Record<string, string>) => void;
  cancel: (id: string) => void;
  retry: (id: string) => void;
  dismiss: (id: string) => void;
  clearFinished: () => void;
  subscribe: (candidateId: string, cb: (e: CompletionEvent) => void) => () => void;
}

const UploadContext = createContext<UploadContextValue | null>(null);

const MAX_PARALLEL = 3;
const ATTACHMENT_LIMIT = 100 * 1024 * 1024;
// Потолок — nginx newats (client_max_body_size 2g), а не ats-api (5 ГБ): файл
// больше него уходил целиком и получал 413 в самом конце. Запрос — это файл
// плюс поля формы, поэтому с запасом в мегабайт.
const MEDIA_LIMIT = 2 * 1024 * 1024 * 1024 - 1024 * 1024;
const TOOL_TRANSCRIBE_LIMIT = 2 * 1024 * 1024 * 1024;

function endpointFor(kind: UploadKind, candidateId: string): string {
  switch (kind) {
    case "media":
      return `/candidates/${candidateId}/upload-recording`;
    case "attachment":
      return `/candidates/${candidateId}/attachments`;
    case "tool-transcribe":
      return `/tools/transcribe`;
  }
}

function limitFor(kind: UploadKind): number {
  switch (kind) {
    case "media":
      return MEDIA_LIMIT;
    case "attachment":
      return ATTACHMENT_LIMIT;
    case "tool-transcribe":
      return TOOL_TRANSCRIBE_LIMIT;
  }
}

// Слишком большой файл: у записи — чем помочь, раз разрезать её за рекрутёра
// мы не можем.
function tooBigText(kind: UploadKind): string {
  const base = `файл больше ${limitLabel(kind)}`;
  return kind === "media" ? `${base} — сервер такие не принимает, сожмите запись` : base;
}

function limitLabel(kind: UploadKind): string {
  switch (kind) {
    case "media":
      return "2 ГБ";
    case "attachment":
      return "100 МБ";
    case "tool-transcribe":
      return "2 ГБ";
  }
}

let counter = 0;
const nextId = () => `up-${Date.now().toString(36)}-${(counter++).toString(36)}`;

export function UploadProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<UploadItem[]>([]);
  // Live XHR handles keyed by item id — kept out of state so React re-renders
  // don't recreate them and so we can abort synchronously from `cancel`.
  const handles = useRef<Map<string, UploadHandle<unknown>>>(new Map());
  const subscribers = useRef<Map<string, Set<(e: CompletionEvent) => void>>>(new Map());

  const patch = useCallback((id: string, changes: Partial<UploadItem>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...changes } : it)));
  }, []);

  const runOne = useCallback(
    (item: UploadItem) => {
      patch(item.id, { status: "uploading", progress: 0, error: null });
      const handle = xhrUploadCancellable<unknown>(
        endpointFor(item.kind, item.candidateId),
        item.file,
        (pct) => patch(item.id, { progress: pct }),
        item.extraFields,
      );
      handles.current.set(item.id, handle);
      handle.promise
        .then((response) => {
          patch(item.id, { status: "done", progress: 100 });
          // Задание расшифровки сохраняется здесь и сейчас, а не только через
          // подписчика ниже: кто бы ни был (не) смонтирован в момент ответа,
          // job_id не должен потеряться — ToolsPage читает этот же модуль.
          if (item.kind === "tool-transcribe") captureToolUploadResponse(response);
          const subs = subscribers.current.get(item.candidateId);
          if (subs) {
            for (const cb of subs) {
              try {
                cb({ candidateId: item.candidateId, kind: item.kind, response });
              } catch {
                /* subscriber errors must not break the queue */
              }
            }
          }
        })
        .catch((err) => {
          const message = err instanceof Error ? err.message : String(err);
          // Distinguish deliberate cancel from a genuine failure — the tray
          // shows a retry button only for real errors.
          const cancelled = message.includes("отменена");
          const error =
            err instanceof ApiError
              ? uploadErrorText(err.status, message, {
                  media: item.kind === "media",
                  meeting: !!item.extraFields?.meeting_id,
                })
              : message;
          patch(item.id, {
            status: cancelled ? "cancelled" : "failed",
            error: cancelled ? null : error,
          });
        })
        .finally(() => {
          handles.current.delete(item.id);
        });
    },
    [patch],
  );

  // Drain scheduler — whenever the queue changes, promote queued items into
  // uploading up to MAX_PARALLEL. Runs after each state update.
  useEffect(() => {
    const active = items.filter((it) => it.status === "uploading").length;
    if (active >= MAX_PARALLEL) return;
    const slots = MAX_PARALLEL - active;
    const next = items.filter((it) => it.status === "queued").slice(0, slots);
    if (next.length === 0) return;
    // Kick outside of a state update to avoid setState-in-render warnings —
    // runOne itself calls setState via patch().
    for (const it of next) runOne(it);
  }, [items, runOne]);

  // beforeunload guard: only nag when something's actually in flight. Attaches
  // once when active items appear, detaches when the queue drains.
  useEffect(() => {
    const hasActive = items.some((it) => it.status === "uploading" || it.status === "queued");
    if (!hasActive) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore custom text and show a generic warning, but the
      // string is still required for older ones (Safari).
      e.returnValue = "Загрузка файлов не завершена — при выходе она прервётся.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    // Сама ради новой версии страница тоже не перезагрузится (lib/appUpdate).
    const release = holdReload();
    return () => {
      window.removeEventListener("beforeunload", handler);
      release();
    };
  }, [items]);

  const enqueue = useCallback(
    (files: FileList | File[], candidateId: string, mediaFields?: Record<string, string>) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      const additions: UploadItem[] = [];
      for (const file of list) {
        const kind: UploadKind = isMediaFile(file.type, file.name) ? "media" : "attachment";
        const extraFields = kind === "media" ? mediaFields : undefined;
        const cap = limitFor(kind);
        const id = nextId();
        if (file.size <= 0) {
          additions.push({
            id,
            file,
            candidateId,
            kind,
            extraFields,
            progress: 0,
            status: "failed",
            error: "пустой файл",
            startedAt: Date.now(),
          });
          continue;
        }
        if (file.size > cap) {
          additions.push({
            id,
            file,
            candidateId,
            kind,
            extraFields,
            progress: 0,
            status: "failed",
            error: tooBigText(kind),
            startedAt: Date.now(),
          });
          continue;
        }
        additions.push({
          id,
          file,
          candidateId,
          kind,
          extraFields,
          progress: 0,
          status: "queued",
          error: null,
          startedAt: Date.now(),
        });
      }
      setItems((prev) => [...prev, ...additions]);
    },
    [],
  );

  const enqueueTool = useCallback(
    (files: FileList | File[], extraFields: Record<string, string>) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      const additions: UploadItem[] = [];
      for (const file of list) {
        const kind: UploadKind = "tool-transcribe";
        const cap = limitFor(kind);
        const id = nextId();
        const base = {
          id,
          file,
          candidateId: TOOL_CTX,
          kind,
          progress: 0,
          startedAt: Date.now(),
          extraFields,
        };
        if (file.size <= 0) {
          additions.push({ ...base, status: "failed", error: "пустой файл" });
          continue;
        }
        if (file.size > cap) {
          additions.push({ ...base, status: "failed", error: tooBigText(kind) });
          continue;
        }
        additions.push({ ...base, status: "queued", error: null });
      }
      setItems((prev) => [...prev, ...additions]);
    },
    [],
  );

  const cancel = useCallback(
    (id: string) => {
      const h = handles.current.get(id);
      if (h) {
        h.abort();
      } else {
        // Not yet started — flip to cancelled directly so the tray hides it.
        patch(id, { status: "cancelled" });
      }
    },
    [patch],
  );

  const retry = useCallback(
    (id: string) => {
      setItems((prev) =>
        prev.map((it) =>
          it.id === id ? { ...it, status: "queued", progress: 0, error: null, startedAt: Date.now() } : it,
        ),
      );
    },
    [],
  );

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  const clearFinished = useCallback(() => {
    setItems((prev) => prev.filter((it) => it.status !== "done" && it.status !== "cancelled"));
  }, []);

  const subscribe = useCallback((candidateId: string, cb: (e: CompletionEvent) => void) => {
    let set = subscribers.current.get(candidateId);
    if (!set) {
      set = new Set();
      subscribers.current.set(candidateId, set);
    }
    set.add(cb);
    return () => {
      const s = subscribers.current.get(candidateId);
      if (!s) return;
      s.delete(cb);
      if (s.size === 0) subscribers.current.delete(candidateId);
    };
  }, []);

  const value = useMemo<UploadContextValue>(
    () => ({ items, enqueue, enqueueTool, cancel, retry, dismiss, clearFinished, subscribe }),
    [items, enqueue, enqueueTool, cancel, retry, dismiss, clearFinished, subscribe],
  );

  return <UploadContext.Provider value={value}>{children}</UploadContext.Provider>;
}

export function useUploads(): UploadContextValue {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error("useUploads must be used within UploadProvider");
  return ctx;
}
